import React from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import ProductListContainer from 'src/components/BeaconV2Components/MetricList/ProductListContainer';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { useCreateRemoveRetailPriceRangeFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useMainTimePeriodShortDisplayName, useComparisonTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import _upperFirst from 'lodash/upperFirst';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import { TrafficNoDataPlaceholder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/pages/NonTopicOrTopicGroupPage';
import { useTrafficAvailability } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/hooks/useTrafficAvailability';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const fields = [
  { name: 'organicClicks', displayName: 'Organic\nTraffic' },
  {
    name: 'adClicks',
    displayName: 'Paid\nTraffic'
  },
  {
    name: 'otherClicks',
    displayName: 'Other\nTraffic'
  },
  {
    name: 'totalClicks',
    displayName: 'Total\nTraffic'
  },
  {
    name: 'adSpend',
    displayName: 'Paid\nAd Spend'
  },
  {
    name: 'adRetailSales',
    displayName: 'Paid\nAd Sales'
  }
];

/**
 * Aggregation builder for getting the number of products that have other traffic
 */
const aggregationBuilder = (aggBuilder: AggregationBuilder): AggregationBuilder => {
  return aggBuilder.clearConditionRangeFilters().addConditionRangeFilter('otherClicks', 1);
};

/**
 * Other Traffic tab page layout
 */
export default function OtherTrafficPage() {
  const { isProductEntity } = useProductEntity();
  const mainEntityType = useMainEntityType();
  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity);
  const weekEndingFields = fields.map(removeComparisonFormatter);
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  const { dataAvailable } = useTrafficAvailability();

  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing({ titleOnly });
  const taxonomyText = useTaxonomySubtitleText();

  const getSubtitle = ({ otherTraffic, formattedPercent, direction }) => {
    const changeDirection = direction < 0 ? 'lower' : 'higher';
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`${_upperFirst(entityPhrase)} earned ${otherTraffic} other traffic `}
          {taxonomyText}
          {` during the ${mainPeriodDisplayName} time period, which is ${formattedPercent} ${changeDirection} compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BasePageContainer>
      {dataAvailable ? (
        <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
          <BeaconComparableSplineChart
            beaconChartWithLegendOverride={
              shouldShowBeaconProTopics()
                ? (props, metric) => {
                    const { formattedPrimaryTotal, formattedPercentChange, primaryTotal, secondaryTotal } = metric;
                    const subtitle = getSubtitle({
                      otherTraffic: formattedPrimaryTotal,
                      formattedPercent: formattedPercentChange,
                      direction: primaryTotal - secondaryTotal
                    });

                    return { ...props, includeSummaryInfo: false, subtitle };
                  }
                : undefined
            }
            indexName="aggregated-traffic"
            fieldName="otherClicks"
            requestBuilder={removeRetailPriceRangeFilters()}
          />
          {!isProductEntity && (
            <BeaconComparableSplineChart
              beaconChartWithLegendOverride={
                shouldShowBeaconProTopics()
                  ? (props) => {
                      return {
                        ...props,
                        includeSummaryInfo: false,
                        subtitle: (
                          <BeaconSubtitleContainer>
                            <Text variant="body1">
                              {`The chart displays the number of products that generated other traffic for `}
                              {entityPhrase} {taxonomyText} during the {mainPeriodDisplayName} period compared to the{' '}
                              {comparisonDisplayName}.
                              <SubtitleFilters />
                            </Text>
                          </BeaconSubtitleContainer>
                        )
                      };
                    }
                  : undefined
              }
              title="Products with Other Traffic"
              indexName="aggregated-traffic"
              fieldName="stacklineSku"
              requestBuilder={removeRetailPriceRangeFilters()}
              primaryAggregationBuilder={aggregationBuilder}
              secondaryAggregationBuilder={aggregationBuilder}
            />
          )}
          {isProductEntity ? (
            <MetricListContainer
              entityTableHeaderProps={{
                title: 'Other Traffic by Week'
              }}
              indexName="aggregated-traffic"
              sortFieldName="otherClicks"
              fields={weekEndingFields}
              headColumnDefinition={getWeekColumnDefinition({
                getTableColumnProps(defaults) {
                  return {
                    ...defaults,
                    style: {
                      ...defaults.style,
                      width: '220px'
                    }
                  };
                }
              })}
              postProcessData={(data) => {
                return data.sort((a, b) => b.weekId.localeCompare(a.weekId));
              }}
              useTableDataProps={{
                groupByFieldName: 'weekId',
                filterResponse: () => true,
                itemsPerPage: 100
              }}
              showGrid={false}
            />
          ) : (
            <ProductListContainer
              entityTableHeaderProps={{
                subTitle: shouldShowBeaconProTopics()
                  ? () => {
                      return (
                        <Text variant="body1" sx={{ marginTop: '16px' }}>
                          {`The table displays the other traffic for `}
                          {entityPhrase} {taxonomyText} by product during the {mainPeriodDisplayName} period compared to the{' '}
                          {comparisonDisplayName}.
                          <SubtitleFilters />
                        </Text>
                      );
                    }
                  : undefined,
                title: 'Other Traffic by Product',
                iconsList: [IconsList.tile, IconsList.table]
              }}
              fields={fields}
              indexName="aggregated-traffic"
              sortFieldName="otherClicks"
              useTableDataProps={{
                buildRequest: removeRetailPriceRangeFilters()
              }}
            />
          )}
        </Flex>
      ) : (
        <TrafficNoDataPlaceholder />
      )}
    </BasePageContainer>
  );
}
